import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { 
  Syringe, 
  Stethoscope, 
  Eye,
  AlertTriangle,
  Baby,
  Scissors,
  DollarSign,
  Skull,
  MapPin,
  CheckCircle,
  XCircle,
  Clock,
  MoreHorizontal
} from 'lucide-react';

// Event type configuration
const eventConfig = {
  vaccination: {
    icon: Syringe,
    color: 'bg-blue-100 text-blue-600 border-blue-200',
    bgColor: 'bg-blue-50',
    label: 'Vaccination',
  },
  treatment: {
    icon: Stethoscope,
    color: 'bg-purple-100 text-purple-600 border-purple-200',
    bgColor: 'bg-purple-50',
    label: 'Treatment',
  },
  observation: {
    icon: Eye,
    color: 'bg-earth-100 text-earth-600 border-earth-200',
    bgColor: 'bg-earth-50',
    label: 'Observation',
  },
  injury: {
    icon: AlertTriangle,
    color: 'bg-orange-100 text-orange-600 border-orange-200',
    bgColor: 'bg-orange-50',
    label: 'Injury',
  },
  calving: {
    icon: Baby,
    color: 'bg-pink-100 text-pink-600 border-pink-200',
    bgColor: 'bg-pink-50',
    label: 'Calving',
  },
  weaning: {
    icon: Scissors,
    color: 'bg-amber-100 text-amber-600 border-amber-200',
    bgColor: 'bg-amber-50',
    label: 'Weaning',
  },
  movement: {
    icon: MapPin,
    color: 'bg-green-100 text-green-600 border-green-200',
    bgColor: 'bg-green-50',
    label: 'Movement',
  },
  sale: {
    icon: DollarSign,
    color: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    bgColor: 'bg-emerald-50',
    label: 'Sale',
  },
  death: {
    icon: Skull,
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    bgColor: 'bg-gray-50',
    label: 'Death',
  },
};

const verificationConfig = {
  verified: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    label: 'Verified',
  },
  pending: {
    icon: Clock,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    label: 'Pending',
  },
  rejected: {
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    label: 'Rejected',
  },
};

function EventCard({ event, isLast }) {
  const config = eventConfig[event.event_type] || eventConfig.observation;
  const verification = verificationConfig[event.verification_status] || verificationConfig.pending;
  const EventIcon = config.icon;
  const VerifyIcon = verification.icon;

  // Parse event data
  const eventData = typeof event.event_data === 'string' 
    ? JSON.parse(event.event_data) 
    : event.event_data || {};

  const eventDate = event.event_date 
    ? parseISO(event.event_date) 
    : new Date(event.created_at);

  return (
    <div className="relative flex gap-4">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-earth-200" />
      )}

      {/* Icon */}
      <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 ${config.color}`}>
        <EventIcon className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className={`flex-1 pb-6 ${isLast ? '' : ''}`}>
        <div className={`p-4 rounded-lg border ${config.bgColor} border-earth-200`}>
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <h4 className="font-medium text-primary-900">{config.label}</h4>
              <p className="text-sm text-earth-500">
                {format(eventDate, 'MMM d, yyyy')} • {formatDistanceToNow(eventDate, { addSuffix: true })}
              </p>
            </div>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${verification.bgColor} ${verification.color}`}>
              <VerifyIcon className="h-3 w-3" />
              {verification.label}
            </div>
          </div>

          {/* Event-specific details */}
          <div className="space-y-1 text-sm">
            {eventData.vaccine_name && (
              <p><span className="text-earth-500">Vaccine:</span> {eventData.vaccine_name}</p>
            )}
            {eventData.treatment_name && (
              <p><span className="text-earth-500">Treatment:</span> {eventData.treatment_name}</p>
            )}
            {eventData.description && (
              <p><span className="text-earth-500">Description:</span> {eventData.description}</p>
            )}
            {eventData.weight_kg && (
              <p><span className="text-earth-500">Weight recorded:</span> {eventData.weight_kg} kg</p>
            )}
            {eventData.notes && (
              <p className="text-earth-600 italic mt-2">{eventData.notes}</p>
            )}
          </div>

          {/* Reporter info */}
          {event.report_channel && (
            <div className="mt-3 pt-3 border-t border-earth-200 text-xs text-earth-500">
              Reported via {event.report_channel}
              {event.reported_by_name && ` by ${event.reported_by_name}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EventTimeline({ events = [], maxEvents = 10, showViewAll = true }) {
  if (!events || events.length === 0) {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-primary-900 mb-4">Event Timeline</h3>
        <div className="py-8 text-center text-earth-500">
          <Eye className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>No events recorded yet</p>
          <p className="text-sm mt-1">Events will appear here as they're logged</p>
        </div>
      </div>
    );
  }

  // Sort events by date (newest first) and limit
  const sortedEvents = [...events]
    .sort((a, b) => new Date(b.event_date || b.created_at) - new Date(a.event_date || a.created_at))
    .slice(0, maxEvents);

  const hasMore = events.length > maxEvents;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-primary-900">Event Timeline</h3>
        <span className="text-sm text-earth-500">{events.length} total events</span>
      </div>

      {/* Timeline */}
      <div className="space-y-0">
        {sortedEvents.map((event, index) => (
          <EventCard 
            key={event.id} 
            event={event} 
            isLast={index === sortedEvents.length - 1}
          />
        ))}
      </div>

      {/* View All Button */}
      {hasMore && showViewAll && (
        <button className="w-full mt-4 py-2 text-sm text-primary-600 hover:text-primary-800 flex items-center justify-center gap-2">
          <MoreHorizontal className="h-4 w-4" />
          View all {events.length} events
        </button>
      )}
    </div>
  );
}
